// Platform-Tokens — statisch, nicht aus Branding.
// Quelle: Scrolly Platform.html (Design-Referenz).

export const PINK = "#FF3366";
export const PINK_DEEP = "#e11d48";

// Schriften-Standard 2026-09-17: Playfair Display (Display) + Barlow Condensed 600
// (Kachel-Titel) + DM Sans (UI), Quelle packages/tokens/scrolly.css. Instrument
// Serif bleibt im Viewer GELADEN (+onRenderHtml.tsx), weil gebackene Studio-SVGs
// und aeltere Scrollys die Familie noch per Name referenzieren.
export const FONT_DISPLAY = '"Playfair Display", Georgia, serif';
/** Titel auf Kacheln/Cards: schmal laufende Grotesk, immer mit fontWeight 600. */
export const FONT_KACHEL = '"Barlow Condensed", "Arial Narrow", "Helvetica Neue", Arial, sans-serif';
// H1-Phase-1.B (2026-05-18): Inter → DM Sans Drift-Elimination.
// DM Sans matches packages/tokens + portal/ + MOCKUPS-INDEX.md Vorgabe;
// Inter was documented fallback from prior import. Web-Font-Loading
// in public/renderer/+onRenderHtml.tsx via Google Fonts in selber Phase.
export const FONT_UI = '"DM Sans", -apple-system, system-ui, sans-serif';

export type VariantId = "editorial" | "immersive";

export const VARIANT: Record<
  VariantId,
  {
    bg: string;
    surface: string;
    text: string;
    muted: string;
    border: string;
    searchBg: string;
    sidebarBg: string;
    sidebarActive: string;
    sidebarHover: string;
  }
> = {
  editorial: {
    bg: "#fafaf7",
    surface: "#ffffff",
    text: "#111111",
    muted: "#6b6b67",
    border: "#eaeae5",
    searchBg: "#f3f3ed",
    sidebarBg: "#fafaf7",
    sidebarActive: "#fef1f4",
    sidebarHover: "#f3f3ed",
  },
  immersive: {
    bg: "#0a0a0a",
    surface: "#141414",
    text: "#ffffff",
    muted: "rgba(255,255,255,0.6)",
    border: "#1f1f1f",
    searchBg: "#161616",
    sidebarBg: "#0a0a0a",
    sidebarActive: "rgba(255,51,102,0.15)",
    sidebarHover: "rgba(255,255,255,0.05)",
  },
};

export const RADIUS = {
  pill: 9999,
  card: 14,
  card_lg: 18,
  card_xl: 20,
  button: 999,
};

export const SHADOW = {
  cardHover: "0 16px 40px rgba(0,0,0,0.15)",
  cardRest: "0 2px 8px rgba(0,0,0,0.06)",
  featured: "0 1px 3px rgba(0,0,0,0.04)",
  deskFrame: "0 40px 120px rgba(0,0,0,0.45)",
};

// ---- Card-Styles --------------------------------------------------------
// Variant-Map für ScrollyCard.
// Quelle: Scrolly Platform.html:1042–1108 (`ScrollyCard`).
//
// Wichtig: Die Quelle rendert **kein Padding** auf der Card-Wrapper —
// der Meta-Block sitzt direkt unter dem Cover mit `marginTop: 10`.
// `cardPadding: 0` ist damit die Quell-Wahrheit, nicht ein Default.
// Shadow + borderRadius + Hover-Lift tragen die visuelle Abgrenzung.

export const CARD_STYLES: Record<
  VariantId,
  {
    /** Card-Wrapper-Padding. 0 = direkt aus der Quelle (siehe Kommentar oben). */
    cardPadding: number;
    /** Cover-Radius innerhalb der Card. */
    coverRadius: number;
    /** Abstand Cover → Meta-Zeile. */
    metaGap: number;
    /** Textfarbe für Creator-Name im Meta-Block. */
    metaText: string;
    /** Sekundärfarbe für Views + Duration. */
    metaMuted: string;
    /** Cover-Fallback-Background (falls Decoration transparent rendert). */
    coverBg: string;
    /** Schatten im Ruhezustand. */
    shadowRest: string;
    /** Schatten beim Hover. */
    shadowHover: string;
  }
> = {
  editorial: {
    cardPadding: 0,
    coverRadius: RADIUS.card,
    metaGap: 10,
    metaText: "#111111",
    metaMuted: "#6b6b67",
    coverBg: "#111111",
    shadowRest: SHADOW.cardRest,
    shadowHover: SHADOW.cardHover,
  },
  immersive: {
    cardPadding: 0,
    coverRadius: RADIUS.card,
    metaGap: 10,
    metaText: "#ffffff",
    metaMuted: "rgba(255,255,255,0.55)",
    coverBg: "#111111",
    shadowRest: SHADOW.cardRest,
    shadowHover: SHADOW.cardHover,
  },
};

// ---- Hero-Styles --------------------------------------------------------
// Variant-Map für FeaturedHero.
// Quelle: Scrolly Platform.html:1119–1187 (`FeaturedHero`).
//
// Wichtige Abweichungen Briefing vs. Quelle:
//   - `padding`: Briefing 40, Quelle '32px 32px'. Wir folgen Briefing (40).
//   - `gap`:     Briefing 44, Quelle 40.           Wir folgen Briefing (44).
//   - `height`:  Briefing 720 fix, Quelle minHeight 380 + maxHeight 480 auf Cover.
//                Wir folgen Briefing (720 fix, responsive via maxWidth).
// Die sonstigen Werte (Pink-Pill-Badge, Cover-Radius 16, Grid 1.4fr 1fr,
// Pill-Typo, Meta-Row-Typo) sind quell-treu.
//
// `ctaBg/ctaText` spiegeln die Quell-Inversion: im Editorial-Variant ist der
// CTA dunkel-auf-hell, im Immersive hell-auf-dunkel.

export const HERO_STYLES: Record<
  VariantId,
  {
    /** Outer-Container-Padding. Briefing-Wert (40), Quelle nutzt 32. */
    padding: number;
    /** Grid-Gap zwischen Text-Spalte und Cover-Spalte. Briefing-Wert (44), Quelle 40. */
    gap: number;
    /** Fixe Container-Höhe. Briefing-Wert (720), Quelle nutzt minHeight 380. */
    height: number;
    /** Max-Breite des Heros im Feed. Briefing sagt 1200×720. */
    maxWidth: number;
    /** Hintergrund des Hero-Containers (Karten-Look). */
    surface: string;
    /** Border um den Container — subtile Abgrenzung. */
    border: string;
    /** Schatten (Editorial nutzt leichten Cardshadow, Immersive keinen). */
    shadow: string;
    /** Primär-Text (Titel + CTA-Hintergrund). */
    text: string;
    /** Sekundärfarbe — Beschreibung + Meta-Row + Follower-Line. */
    muted: string;
    /** Hintergrund der CTA-Pill ("Scroll starten"). */
    ctaBg: string;
    /** Textfarbe der CTA-Pill. */
    ctaText: string;
    /** Cover-Radius innerhalb des Heros. Quelle: borderRadius 16. */
    coverRadius: number;
    /** Aspect des großen Hero-Covers. Quelle: 3/4. */
    coverAspect: "3/4" | "9/16";
  }
> = {
  editorial: {
    padding: 40,
    gap: 44,
    height: 720,
    maxWidth: 1200,
    surface: "#ffffff",
    border: "1px solid #eaeae5",
    shadow: SHADOW.featured,
    text: "#111111",
    muted: "#5a5a55",
    ctaBg: "#111111",
    ctaText: "#ffffff",
    coverRadius: 16,
    coverAspect: "3/4",
  },
  immersive: {
    padding: 40,
    gap: 44,
    height: 720,
    maxWidth: 1200,
    surface: "#141414",
    border: "1px solid #222222",
    shadow: "none",
    text: "#ffffff",
    muted: "rgba(255,255,255,0.7)",
    ctaBg: "#ffffff",
    ctaText: "#111111",
    coverRadius: 16,
    coverAspect: "3/4",
  },
};

// ---- Source-Badge-Styles ------------------------------------------------
// Phase 11.5 (2026-04-22).
// Kleines "via {Publisher}"-Chip unten-rechts auf dem Cover, wenn
// `scrolly.source !== "native"`. Sitzt auf dem Cover (dunkles Bild), nicht
// auf der Card-Background-Fläche — deshalb ist die Basis-Palette auf dunkle
// Cover abgestimmt (weiße Typo, halbtransparente dunkle Fläche).
//
// Variant-Unterschied ist subtil: Editorial nutzt etwas weicheren Kontrast,
// Immersive schärferen. Der Badge soll nicht wie Werbung wirken, deshalb
// keine Pink-Akzentfarbe, keine Full-Opacity-Fläche.
//
// Briefing (HANDOFF-PROMPT-PHASE-11-STEP-4.md, Block 0):
//   "klein, halbtransparent, nicht wie Werbung … variant-aware".

export const SOURCE_BADGE_STYLES: Record<
  VariantId,
  {
    /** Hintergrundfläche — halbtransparent, über Cover. */
    bg: string;
    /** Textfarbe — Weiß-Derivat für Kontrast auf Cover. */
    color: string;
    /** Border um das Chip, sehr dezent. */
    border: string;
    /** Font-Size im Chip. */
    fontSize: number;
    /** Inner-Padding. */
    padding: string;
    /** Corner-Radius. Pill-Look (999). */
    radius: number;
    /** Abstand zum Cover-Rand (rechts + unten). */
    offset: number;
  }
> = {
  editorial: {
    bg: "rgba(0, 0, 0, 0.45)",
    color: "rgba(255, 255, 255, 0.88)",
    border: "1px solid rgba(255, 255, 255, 0.14)",
    fontSize: 10.5,
    padding: "3px 9px",
    radius: 999,
    offset: 10,
  },
  immersive: {
    bg: "rgba(0, 0, 0, 0.55)",
    color: "rgba(255, 255, 255, 0.92)",
    border: "1px solid rgba(255, 255, 255, 0.18)",
    fontSize: 10.5,
    padding: "3px 9px",
    radius: 999,
    offset: 10,
  },
};

// ---- Cover-Paletten -----------------------------------------------------
// Gradient-Hintergrund + Accent-Color pro Kategorie.
// Quelle: handoff/phase-11/cover-variants.jsx (COVER_PALETTES).
// Keys spiegeln `PaletteKey` aus lib/mock/scrollys.ts.

export type CoverPalette = {
  bg: string;
  accent: string;
};

export const COVER_PALETTES: Record<string, CoverPalette> = {
  data: {
    bg: "linear-gradient(160deg, #1a0a1a 0%, #3a0a24 60%, #FF3366 140%)",
    accent: "#ffb3c5",
  },
  journalism: {
    bg: "linear-gradient(180deg, #1a1f2b 0%, #2e3a4e 60%, #5a6e8a 100%)",
    accent: "#e8d5a8",
  },
  science: {
    bg: "linear-gradient(180deg, #0e2a3d 0%, #1a4a6a 60%, #7fb5d5 120%)",
    accent: "#b8e0f5",
  },
  design: {
    bg: "linear-gradient(180deg, #2d1b3d 0%, #8b5a8c 60%, #d4a5a5 100%)",
    accent: "#ffd6d6",
  },
  history: {
    bg: "linear-gradient(160deg, #2a1a0a 0%, #6b3e1a 60%, #d4a574 120%)",
    accent: "#fde68a",
  },
  travel: {
    bg: "linear-gradient(180deg, #e8e4d9 0%, #a8bfd8 40%, #2e4a6a 100%)",
    accent: "#ffffff",
  },
  business: {
    bg: "linear-gradient(160deg, #1a0a1f 0%, #5a1a3a 60%, #EC4899 140%)",
    accent: "#fbcfe8",
  },
  howto: {
    bg: "linear-gradient(135deg, #f5e6d3 0%, #d4a574 50%, #6b4226 100%)",
    accent: "#ffffff",
  },
  brand: {
    bg: "linear-gradient(135deg, #322832 0%, #1a1a1a 100%)",
    accent: "#ffb3c5",
  },
};
